/**
 * EllipsisText — one line of text that ends in "…" when it does not fit, and
 * shows the full text in a tooltip ONLY when it was actually cut off.
 *
 * Why measure instead of always attaching a tooltip: a tooltip on text that is
 * already fully visible just repeats it, which is noise — and the native
 * `title` attribute this replaces did exactly that on every row.
 *
 * "Cut off" is decided from the real layout (scrollWidth > clientWidth), and
 * re-checked whenever the box is resized (sidebar width, window size) or the
 * text changes, so the tooltip appears and disappears as the space changes.
 *
 * The text is always fully present in the DOM, so screen readers read the whole
 * value whether or not it is visually cut off — the tooltip is for sighted users.
 */
import { useCallback, useLayoutEffect, useState } from 'react';

import { FloatingTooltip } from '@components/common/FloatingTooltip';
import { cx } from '@lib/classNames';

import styles from './EllipsisText.module.css';

/** Is the element's text wider than the space it has? */
function isTruncated(el) {
  return Boolean(el) && el.scrollWidth > el.clientWidth;
}

export function EllipsisText({ children, side = 'bottom', className }) {
  const [el, setEl] = useState(null);
  const [truncated, setTruncated] = useState(false);

  // A callback ref, because the element is replaced when the tooltip wrapper
  // is added or removed, and the measurement must follow the new one.
  const ref = useCallback((node) => setEl(node), []);

  useLayoutEffect(() => {
    if (!el) return undefined;
    const measure = () => setTruncated(isTruncated(el));
    measure();

    // Re-measure when the box changes size. Falls back to window resize where
    // ResizeObserver is unavailable (older browsers, jsdom).
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measure);
      observer.observe(el);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [el, children]);

  const text = (
    <span ref={ref} className={cx(styles.text, className)}>
      {children}
    </span>
  );

  if (!truncated) return text;

  // Floating, not the in-place library Tooltip: ellipsised text usually lives in
  // a scrolling list, which would clip an in-place bubble at its edges.
  return (
    <FloatingTooltip label={children} side={side} className={styles.tip}>
      {text}
    </FloatingTooltip>
  );
}

export default EllipsisText;
